import { isEqual, startOfDay, addMinutes } from 'date-fns';
import { timeToMinutes, minutesToTime } from './time';
import { DEFAULT_OPEN_TIME, DEFAULT_CLOSE_TIME } from './constants';

export interface TimeConstraints {
    minTime: string;
    maxTime: string;
    breakStart: string | null;
    breakEnd: string | null;
    isClosed: boolean;
    closingTime: string;
}

export interface ValidationContext {
    bookings: any[];
    performances: any[];
    busySlots: any[];
    organization: any;
    users: any[];
    shiftChanges?: any[];
}

export interface ValidationParams {
    date: Date;
    time: string;
    performanceId: string;
    staffId: string;
    excludeBookingId?: string | number;
}

export interface ValidationResult {
    isValid: boolean;
    errorKey?: string;
}

/**
 * Calcola i vincoli orari combinati di salone e dipendente per una data e un servizio specifici.
 */
export function calculateTimeConstraints(
    date: Date,
    organization: any,
    staffId: string,
    users: any[],
    performanceId: string,
    performances: any[],
    shiftChanges?: any[]
): TimeConstraints {
    let minTime = DEFAULT_OPEN_TIME;
    let maxTime = DEFAULT_CLOSE_TIME;
    let breakStart: string | null = null;
    let breakEnd: string | null = null;
    let isClosed = false;

    if (date && organization?.time_ranges) {
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayName = days[date.getDay()];
        const shopSched = organization.time_ranges[dayName];
        const constraintsEnabled = organization.constraints_enabled !== false;
        const dStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

        // Il cambio turno prevale su tutto: ignora sia l'orario del salone sia quello normale dello staff.
        // Funziona anche su giorni normalmente chiusi.
        if (constraintsEnabled && staffId && shiftChanges?.length) {
            const shiftChange = shiftChanges.find((sc: any) =>
                sc.shift_date === dStr && sc.hairdresser_id === staffId
            );
            if (shiftChange) {
                minTime = shiftChange.start_time.slice(0, 5);
                maxTime = shiftChange.end_time.slice(0, 5);
                breakStart = shiftChange.start_break ? shiftChange.start_break.slice(0, 5) : null;
                breakEnd = shiftChange.end_break ? shiftChange.end_break.slice(0, 5) : null;
                isClosed = false;

                const closingTime = maxTime;
                if (performanceId) {
                    const perf = performances?.find((p: any) => p.id === performanceId);
                    if (perf) {
                        const endMin = timeToMinutes(maxTime);
                        const latestStartMin = endMin - (perf.duration || 0);
                        maxTime = minutesToTime(latestStartMin);
                        if (latestStartMin < timeToMinutes(minTime)) isClosed = true;
                    }
                }
                return { minTime, maxTime, breakStart, breakEnd, isClosed, closingTime };
            }
        }

        // Nessun cambio turno: usa orario salone + orario normale staff
        if (!shopSched || !shopSched.open) {
            isClosed = true;
        } else {
            minTime = shopSched.open.slice(0, 5);
            maxTime = shopSched.close ? shopSched.close.slice(0, 5) : DEFAULT_CLOSE_TIME;

            if (constraintsEnabled) {
                breakStart = shopSched.start_break?.slice(0, 5) || null;
                breakEnd = shopSched.end_break?.slice(0, 5) || null;

                if (staffId) {
                    const staff = users.find((u: any) => u.id === staffId);
                    const staffSched = staff?.time_ranges?.[dayName];

                    if (!staffSched || !staffSched.open) {
                        isClosed = true;
                        return { minTime, maxTime, breakStart, breakEnd, isClosed, closingTime: maxTime };
                    }

                    // Intersezione turno staff con orario salone
                    const shopOpenMins = timeToMinutes(minTime);
                    const staffOpenMins = timeToMinutes(staffSched.open.slice(0, 5));
                    minTime = minutesToTime(Math.max(shopOpenMins, staffOpenMins));

                    const shopCloseMins = timeToMinutes(maxTime);
                    const staffCloseMins = timeToMinutes(staffSched.close?.slice(0, 5) || DEFAULT_CLOSE_TIME);
                    maxTime = minutesToTime(Math.min(shopCloseMins, staffCloseMins));

                    // Pausa: unione della pausa del salone con la pausa dello staff
                    if (staffSched.start_break && staffSched.end_break) {
                        const staffBStartMins = timeToMinutes(staffSched.start_break.slice(0, 5));
                        const staffBEndMins = timeToMinutes(staffSched.end_break.slice(0, 5));
                        if (breakStart !== null && breakEnd !== null) {
                            breakStart = minutesToTime(Math.min(timeToMinutes(breakStart), staffBStartMins));
                            breakEnd = minutesToTime(Math.max(timeToMinutes(breakEnd), staffBEndMins));
                        } else {
                            breakStart = staffSched.start_break.slice(0, 5);
                            breakEnd = staffSched.end_break.slice(0, 5);
                        }
                    }
                }
            }
        }
    }

    const closingTime = maxTime;

    if (date && organization?.time_ranges && !isClosed && performanceId) {
        const perf = performances?.find((p: any) => p.id === performanceId);
        if (perf) {
            const endMin = timeToMinutes(maxTime);
            const latestStartMin = endMin - (perf.duration || 0);
            maxTime = minutesToTime(latestStartMin);
            if (latestStartMin < timeToMinutes(minTime)) isClosed = true;
        }
    }

    return { minTime, maxTime, breakStart, breakEnd, isClosed, closingTime };
}

export function checkBookingConflict(
    bookings: any[],
    performances: any[],
    staffId: string,
    dateStr: string,
    targetStartMins: number,
    targetEndMins: number,
    excludeBookingId?: string | number
): boolean {
    const staffBookings = bookings.filter((b: any) =>
        b.hairdresser_id === staffId &&
        b.date === dateStr &&
        b.status !== 'cancelled' &&
        b.status !== 'no_show' &&
        b.id != excludeBookingId
    );

    return staffBookings.some((b: any) => {
        const bPerf = performances.find((p: any) => p.id === b.performance_id);
        const bStartMins = timeToMinutes(b.time.slice(0, 5));
        const bEndMins = bStartMins + (bPerf?.duration || 0);

        return (
            (targetStartMins >= bStartMins && targetStartMins < bEndMins) ||
            (targetEndMins > bStartMins && targetEndMins <= bEndMins) ||
            (targetStartMins <= bStartMins && targetEndMins >= bEndMins)
        );
    });
}

export function checkBusyConflict(
    busySlots: any[],
    staffId: string,
    targetDayStart: Date,
    slotStartISO: Date,
    slotEndISO: Date
): boolean {
    return busySlots.some((bs: any) => {
        if (bs.type !== 'permit' && bs.type !== 'holiday' && bs.type !== 'block') return false;
        if (bs.hairdresser_id && bs.hairdresser_id !== staffId) return false;

        const parseLocalDate = (dateStr: string) => {
            if (!dateStr) return new Date();
            const [y, m, d] = dateStr.split('T')[0].split('-').map(Number);
            return new Date(y, m - 1, d);
        };

        const permitStartDate = parseLocalDate(bs.start_date);

        // Seleziona tutto il giorno se è una holiday, is_all_day è true, o mancano gli orari
        if (bs.type === 'holiday' || bs.is_all_day || (!bs.start_time && !bs.end_time)) {
            const permitEndDate = bs.end_date ? parseLocalDate(bs.end_date) : permitStartDate;
            const permitStartDay = startOfDay(permitStartDate);
            const permitEndDay = startOfDay(permitEndDate);

            if (targetDayStart >= permitStartDay && targetDayStart <= permitEndDay) {
                return true;
            }
            return false;
        }

        // È un permesso o evento con orari specifici
        if (bs.start_time && bs.end_time) {
            // Se non è lo stesso giorno, nessun conflitto orario (i permessi a ore sono giornalieri
            // nel contesto del TimePicker / UI attuale)
            if (!isEqual(startOfDay(permitStartDate), targetDayStart)) return false;

            const permitStartMins = timeToMinutes(bs.start_time.slice(0, 5));
            const permitEndMins = timeToMinutes(bs.end_time.slice(0, 5));

            const slotStartMins = slotStartISO.getHours() * 60 + slotStartISO.getMinutes();
            const slotEndMins = slotEndISO.getHours() * 60 + slotEndISO.getMinutes();

            return slotStartMins < permitEndMins && slotEndMins > permitStartMins;
        }

        return false;
    });
}

/**
 * Valida un intero blocco appuntamento considerando orari del salone, staff, pause, prenotazioni esistenti e permessi.
 */
export function validateBookingSlot(
    context: ValidationContext,
    params: ValidationParams
): ValidationResult {
    const { bookings, performances, busySlots, organization, users, shiftChanges } = context;
    const { date, time, performanceId, staffId, excludeBookingId } = params;

    const perf = performances.find((p: any) => p.id === performanceId);
    if (!perf) {
        return { isValid: false, errorKey: 'calendar.no_service_for_staff' };
    }

    const duration = perf.duration || 0;
    const targetStartMins = timeToMinutes(time);
    const targetEndMins = targetStartMins + duration;

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const dateStr = `${yyyy}-${mm}-${dd}`;

    // Vincoli effettivi: cambio turno (se presente) sostituisce l'orario normale dello staff
    const constraints = calculateTimeConstraints(date, organization, staffId, users, performanceId, performances, shiftChanges);

    const constraintsEnabled = organization?.constraints_enabled !== false;

    // Finestra di disponibilità extra (BusySlots type 'extra'): uno slot sbloccato
    // dall'agenda è valido anche fuori da orari, pause e giorni di chiusura.
    // Restano comunque attivi i controlli su appuntamenti sovrapposti e ferie/permessi.
    const fitsExtraWindow = (busySlots || []).some((bs: any) => {
        if (bs.type !== 'extra' || bs.hairdresser_id !== staffId) return false;
        if (!bs.start_time || !bs.end_time) return false;
        const s = (bs.start_date || '').slice(0, 10);
        const e = ((bs.end_date || bs.start_date) || '').slice(0, 10);
        if (s > dateStr || e < dateStr) return false;
        return timeToMinutes(bs.start_time.slice(0, 5)) <= targetStartMins
            && timeToMinutes(bs.end_time.slice(0, 5)) >= targetEndMins;
    });

    if (!fitsExtraWindow) {
        if (constraints.isClosed) {
            return { isValid: false, errorKey: 'calendar.shop_closed' };
        }

        if (targetStartMins < timeToMinutes(constraints.minTime) || targetStartMins > timeToMinutes(constraints.maxTime)) {
            return { isValid: false, errorKey: 'calendar.outside_hours' };
        }

        // Controllo Pausa
        if (constraintsEnabled && constraints.breakStart && constraints.breakEnd) {
            const breakStartMins = timeToMinutes(constraints.breakStart);
            const breakEndMins = timeToMinutes(constraints.breakEnd);

            if (
                (targetStartMins >= breakStartMins && targetStartMins < breakEndMins) ||
                (targetEndMins > breakStartMins && targetEndMins <= breakEndMins) ||
                (targetStartMins <= breakStartMins && targetEndMins >= breakEndMins)
            ) {
                return { isValid: false, errorKey: 'calendar.overlap_break' };
            }
        }
    }

    // 4. Overlap Prenotazioni
    if (constraintsEnabled) {
        const hasBookingConflict = checkBookingConflict(
            bookings, performances, staffId, dateStr, targetStartMins, targetEndMins, excludeBookingId
        );

        if (hasBookingConflict) {
            return { isValid: false, errorKey: 'calendar.overlap_booking' };
        }
    }

    // 5. Overlap Ferie/Permessi (busySlots)
    const slotStartISO = new Date(`${dateStr}T${time}:00`);
    const slotEndISO = addMinutes(slotStartISO, duration);
    const targetDayStart = startOfDay(slotStartISO);

    if (constraintsEnabled) {
        const hasBusyConflict = checkBusyConflict(
            busySlots, staffId, targetDayStart, slotStartISO, slotEndISO
        );

        if (hasBusyConflict) {
            return { isValid: false, errorKey: 'calendar.overlap_vacation' };
        }
    }

    return { isValid: true };
}
